import { useCallback } from "react";

import { MessageItem } from "@/components/cached-messages/types";
import { useNotifications } from "@/contexts/NotificationsContext";

import { ApiError, useApi } from "../useApi";

/**
 * How long a submission is allowed to take before it is reported as failed.
 *
 * Bounds the *outcome*, not the HTTP request -- see `updateSqlQuery`.
 */
export const SQL_EDIT_TIMEOUT_MS = 30_000;

/** Shown when the request never produced a response. */
export const TIMEOUT_REASON = "The request did not complete. Please try again.";

/** Shown when the server failed without a readable reason. */
export const FALLBACK_FAILURE_REASON =
  "The SQL query could not be updated. Please try again.";

export const SUCCESS_MESSAGE = "SQL query updated.";

/**
 * The partial-success message.
 *
 * Deliberately distinct from both the success and the failure text: the query
 * *was* stored, so telling the admin nothing was saved would be wrong and would
 * invite a duplicate resubmission; but the entry is no longer serving, so
 * reporting plain success would be wrong too.
 */
export const PARTIAL_MESSAGE =
  "The stored query was updated, but the active cache was not. " +
  "The entry has been deactivated -- re-cache it to serve the new query.";

export const PARTIAL_HEADER = "Cache not updated";
export const FAILURE_HEADER = "SQL update failed";

/**
 * The result of a submission.
 *
 * `success` and `partial` both carry the entry as the server stored it, which is
 * what the page renders. `failed` carries a reason to show and nothing else --
 * there is no entry, because nothing was stored.
 */
export type SqlEditOutcome =
  | { kind: "success"; entry: MessageItem }
  | { kind: "partial"; entry: MessageItem }
  | { kind: "failed"; reason: string };

/** The status the endpoint uses for "stored, but the cache disagrees". */
const PARTIAL_STATUS_CODE = 502;

interface PartialBody {
  message?: string;
  sqlUpdated?: boolean;
  cacheUpdated?: boolean;
  entry?: MessageItem;
}

/**
 * Parse the server's own words out of an error body.
 *
 * `useApi` hands back `body` as raw text rather than parsed JSON, so a caller
 * that wants the server's reason has to parse it -- and has to survive a body
 * that is not JSON at all (a gateway's HTML error page, or an empty body on a
 * network failure).
 */
function parseErrorBody(body: string | undefined): PartialBody | null {
  if (!body) {
    return null;
  }
  try {
    const parsed = JSON.parse(body);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as PartialBody)
      : null;
  } catch {
    return null;
  }
}

function isApiError(error: unknown): error is ApiError {
  return typeof error === "object" && error !== null && "message" in error;
}

/**
 * Persists an admin's edit to one cached message's stored SQL query.
 *
 * Follows `useAdminMetadataFields`'s shape, including its reasoning about
 * attribution: the client never knows its own admin identity, because the server
 * derives it from the authorizer context on the write. So nothing here is
 * applied optimistically -- not the SQL, not the timestamp, not the attributor.
 * Every one of them is read back from the response.
 */
const useAdminCachedMessageSqlEdit = () => {
  // isAdmin: true sends the access token the admin authorizer validates; the ID
  // token would be rejected.
  const { apiPatch } = useApi({ isAdmin: true });
  const { notifySuccess, notifyError } = useNotifications();

  /**
   * Submit one edit.
   *
   * Resolves for `success` and for `partial`, and **rejects** for `failed`.
   * That asymmetry is the contract the modal is built on: it closes when this
   * promise resolves and stays open with the admin's text intact when it
   * rejects. Driving the close from the promise's outcome rather than from a
   * flag the caller has to inspect means a caller cannot report a failure and
   * discard the admin's work in the same breath.
   *
   * The timeout is an `AbortController` whose abort rejects the race, and it is
   * worth being precise about what that does and does not do: Amplify's REST
   * client takes no abort signal, so this bounds how long the *admin* waits for
   * an outcome, not how long the request lives. A response arriving after the
   * timeout is simply never read -- nothing is written from it, so a late
   * success cannot overwrite the page.
   */
  const updateSqlQuery = useCallback(
    async (messageId: string, sqlQuery: string): Promise<SqlEditOutcome> => {
      const controller = new AbortController();
      const timer = setTimeout(
        () => controller.abort(),
        SQL_EDIT_TIMEOUT_MS,
      );

      const timedOut = new Promise<never>((_resolve, reject) => {
        controller.signal.addEventListener("abort", () =>
          reject({ timedOut: true }),
        );
      });

      try {
        const entry = await Promise.race([
          // The text is sent exactly as entered. Any trimming or reformatting
          // here would store a query the admin did not write.
          apiPatch<MessageItem>(`/admin/cached-messages/${messageId}`, {
            sqlQuery,
          }),
          timedOut,
        ]);

        if (!entry) {
          // A 2xx with no body. Nothing to render, so this is not a success.
          const outcome: SqlEditOutcome = {
            kind: "failed",
            reason: FALLBACK_FAILURE_REASON,
          };
          notifyError(FAILURE_HEADER, outcome.reason);
          throw outcome;
        }

        notifySuccess(SUCCESS_MESSAGE);
        return { kind: "success", entry };
      } catch (error) {
        // Re-throw an outcome this function already built and reported.
        if (
          typeof error === "object" &&
          error !== null &&
          (error as SqlEditOutcome).kind === "failed"
        ) {
          throw error;
        }

        if (
          typeof error === "object" &&
          error !== null &&
          (error as { timedOut?: boolean }).timedOut
        ) {
          const outcome: SqlEditOutcome = {
            kind: "failed",
            reason: TIMEOUT_REASON,
          };
          notifyError(FAILURE_HEADER, outcome.reason);
          throw outcome;
        }

        const apiError = isApiError(error) ? error : undefined;
        const parsed = parseErrorBody(apiError?.body);

        // The partial case arrives as a *thrown* error, because every non-2xx
        // does: `useApi` rejects on status alone. So it has to be recognised by
        // its status code and then read out of the raw body, rather than
        // arriving as a resolved value.
        if (apiError?.statusCode === PARTIAL_STATUS_CODE && parsed?.entry) {
          notifyError(PARTIAL_HEADER, parsed.message || PARTIAL_MESSAGE);
          return { kind: "partial", entry: parsed.entry };
        }

        // The server's own reason, which Phase 3 made reachable. Falls back
        // only when the body carried none.
        const reason =
          parsed?.message?.trim() ||
          apiError?.message?.trim() ||
          FALLBACK_FAILURE_REASON;
        const outcome: SqlEditOutcome = { kind: "failed", reason };
        notifyError(FAILURE_HEADER, reason);
        throw outcome;
      } finally {
        clearTimeout(timer);
      }
    },
    [apiPatch, notifySuccess, notifyError],
  );

  return { updateSqlQuery };
};

export default useAdminCachedMessageSqlEdit;
